/*
 * Case Study Image Slider (sections/case-study-main-v2.liquid, block type image_slider)
 *
 * Native scroll-snap carousel: arrows advance one slide, pointer drag scrolls with inertia,
 * touch swipe is the browser's own. No GSAP dependency: layout/theme.liquid:155 does load GSAP
 * on metaobject templates, but scroll-snap gives correct keyboard and touch behaviour for free
 * and keeps the slider working even when GSAP is absent or still loading.
 *
 * Figma 4302:34041 rests with the middle slide centred and its neighbours peeking, so a
 * 3-slide track is centred on init.
 *
 * The loop is seamless rather than a rewind: the slide set is cloned either side of the real
 * one, and whenever the centred slide is a clone the track is silently re-centred on its twin
 * in the real set. Every arrow press therefore moves one slide in the pressed direction
 * forever, with no visible jump and no disabled end state.
 *
 * That re-centring is the whole reason this file is delicate. It writes scrollLeft, and a
 * scrollLeft write is only invisible when the browser is not itself scrolling the track and
 * the landing offset is exactly a snap position. Both conditions are enforced below.
 */
(function () {
  var SLIDER_SEL = '[data-csm2-slider]';
  var TRACK_SEL = '[data-csm2-slider-track]';
  var SLIDE_SEL = '[data-csm2-slide]';
  var CLONE_SEL = '[data-csm2-clone]';
  var PANEL_SEL = '.csm2__slider-panel';
  var INIT_FLAG = 'csm2SliderInit';
  var DRAG_THRESHOLD = 5;
  // Consecutive frames of a completely still track before the re-centre is applied. Scroll has
  // to fully stop first, or the write cuts a smooth scroll short and fights the browser. iOS
  // delivers scroll events in bursts with gaps inside a single flick, so stillness is observed
  // over several frames rather than inferred from one gap in event delivery.
  var SETTLE_FRAMES = 6;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function cloneSlide(slide) {
    var clone = slide.cloneNode(true);
    clone.setAttribute('data-csm2-clone', '');
    // The duplicates are decoration: screen readers should only meet each screen once.
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    return clone;
  }

  function initSlider(slider) {
    if (!slider || slider.dataset[INIT_FLAG] === '1') return;
    slider.dataset[INIT_FLAG] = '1';

    var track = slider.querySelector(TRACK_SEL);
    if (!track) return;

    // Excluding clones keeps this the real set even when re-initialising over a track that has
    // already been cloned, so the set count below is never inflated by the duplicates.
    var slides = track.querySelectorAll(SLIDE_SEL + ':not(' + CLONE_SEL + ')');
    if (!slides.length) return;

    var panel = slider.closest(PANEL_SEL);
    var prevBtn = panel ? panel.querySelector('[data-csm2-slider-prev]') : null;
    var nextBtn = panel ? panel.querySelector('[data-csm2-slider-next]') : null;

    // ----- Clone the set either side so the track has no first or last slide -----
    // Three sets in all: [clones][real][clones]. That gives a full set of runway on each side,
    // which is more than one step or one drag frame can consume before the next wrap.
    var looping = slides.length > 1;
    if (looping && !track.querySelector(CLONE_SEL)) {
      var lead = document.createDocumentFragment();
      var tail = document.createDocumentFragment();
      Array.prototype.forEach.call(slides, function (slide) {
        lead.appendChild(cloneSlide(slide));
        tail.appendChild(cloneSlide(slide));
      });
      track.insertBefore(lead, slides[0]);
      track.appendChild(tail);
    }

    // ----- Step size: one slide plus the flex gap -----
    function step() {
      var first = slides[0];
      var slideWidth = first ? first.getBoundingClientRect().width : 0;
      var gap = 0;
      if (slides.length > 1) {
        var a = slides[0].getBoundingClientRect();
        var b = slides[1].getBoundingClientRect();
        gap = Math.max(0, b.left - a.right);
      }
      return slideWidth + gap;
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    // Scroll offset that centres `el`. Measured against the track rather than offsetLeft,
    // which resolves against the nearest positioned ancestor and not this scroll container.
    // Layout-based, so the result does not shift as the track scrolls.
    function centreOffsetOf(el) {
      var trackRect = track.getBoundingClientRect();
      var rect = el.getBoundingClientRect();
      var offsetInContent = rect.left - trackRect.left + track.scrollLeft;
      return offsetInContent - (track.clientWidth - rect.width) / 2;
    }

    // Every slide in DOM order: [clones][real][clones], so the real set occupies indices
    // [n, 2n). Queried lazily, i.e. after the cloning above, unlike `slides` — a static
    // NodeList captured before it, which therefore still holds the real set alone.
    var allSlides = null;

    function slideList() {
      if (!allSlides) allSlides = track.querySelectorAll(SLIDE_SEL);
      return allSlides;
    }

    // Index of the slide sitting closest to the middle of the track.
    function centredIndex() {
      var list = slideList();
      var trackRect = track.getBoundingClientRect();
      var middle = trackRect.left + trackRect.width / 2;
      var best = -1;
      var bestDistance = Infinity;
      for (var i = 0; i < list.length; i++) {
        var rect = list[i].getBoundingClientRect();
        var distance = Math.abs(rect.left + rect.width / 2 - middle);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      return best;
    }

    // Re-centre on the twin of the centred slide inside the real set, and return the delta
    // applied so a caller holding its own scroll origin can rebase. There is a full set of
    // runway either side, so a single step can never reach past the clones.
    //
    // The landing offset comes out of layout rather than out of a slide width times a count:
    // an arithmetic target built from fractional widths lands a fraction off a snap position,
    // and under `scroll-snap-type: mandatory` the browser then animates the correction. A
    // measured offset is exactly a snap position, so there is nothing left to correct.
    function wrap() {
      if (!looping) return 0;
      var count = slides.length;
      var index = centredIndex();
      if (index < 0 || (index >= count && index < count * 2)) return 0;
      var target = slideList()[count + (index % count)];
      if (!target) return 0;
      var from = track.scrollLeft;
      var to = Math.max(0, Math.min(centreOffsetOf(target), maxScroll()));
      if (Math.abs(to - from) < 0.5) return 0;
      // Snapping and smooth behaviour each animate a scrollLeft write, and either animation
      // exposes the seam. Suppress both for the write and flush layout before restoring, or
      // the restore re-snaps from the position we just left. Restoring to '' hands
      // scroll-snap-type back to the stylesheet, which keeps `.is-dragging` correct mid-drag.
      var previousBehavior = track.style.scrollBehavior;
      var previousSnap = track.style.scrollSnapType;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.scrollLeft = to;
      void track.offsetWidth;
      track.style.scrollSnapType = previousSnap;
      track.style.scrollBehavior = previousBehavior;
      // Read back rather than trusting `to`: the clamp above may have shortened the write.
      return track.scrollLeft - from;
    }

    function scrollByStep(direction) {
      var delta = step() * direction;
      if (typeof track.scrollBy === 'function') {
        track.scrollBy({
          left: delta,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      } else {
        track.scrollLeft += delta;
      }
    }

    // Wrap before the smooth scroll, never during: re-pointing scrollLeft mid-animation
    // would cancel it. Doing it first also guarantees the step stays inside the runway.
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        stopInertia();
        cancelSettle();
        wrap();
        scrollByStep(-1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        stopInertia();
        cancelSettle();
        wrap();
        scrollByStep(1);
      });
    }

    // Native scrolling (touch swipe, trackpad, keyboard on the focused track) wraps once the
    // motion stops. Drag and inertia wrap inline instead, so they opt out here.
    //
    // "Stopped" is measured rather than assumed, because on a phone nothing else is guarding
    // this: `dragActive` is only ever true for a mouse, since the pointerdown handler below
    // hands touch straight back to the browser. Sampling scrollLeft until it has held still
    // for several frames covers both a finger resting mid-swipe and the quiet stretches
    // inside iOS momentum, either of which a fixed idle timeout mistakes for the end.
    var settleRaf = null;
    var settleLast = -1;
    var settleStill = 0;
    var touchActive = false;

    function cancelSettle() {
      if (settleRaf !== null) {
        cancelAnimationFrame(settleRaf);
        settleRaf = null;
      }
      settleLast = -1;
      settleStill = 0;
    }

    function settleTick() {
      settleRaf = null;
      if (touchActive || dragActive || inertiaRaf !== null) {
        settleLast = -1;
        settleStill = 0;
        return;
      }
      var now = track.scrollLeft;
      settleStill = settleLast >= 0 && Math.abs(now - settleLast) < 0.5 ? settleStill + 1 : 0;
      settleLast = now;
      if (settleStill >= SETTLE_FRAMES) {
        settleLast = -1;
        settleStill = 0;
        wrap();
        return;
      }
      settleRaf = requestAnimationFrame(settleTick);
    }

    function watchSettle() {
      if (settleRaf !== null) return;
      settleLast = -1;
      settleStill = 0;
      settleRaf = requestAnimationFrame(settleTick);
    }

    track.addEventListener(
      'scroll',
      function () {
        if (dragActive || inertiaRaf !== null) return;
        watchSettle();
      },
      { passive: true }
    );

    if ('onscrollend' in track) {
      track.addEventListener('scrollend', function () {
        if (touchActive || dragActive || inertiaRaf !== null) return;
        cancelSettle();
        wrap();
      });
    }

    track.addEventListener(
      'touchstart',
      function () {
        touchActive = true;
        cancelSettle();
      },
      { passive: true }
    );

    function onTouchEnd() {
      touchActive = false;
      // Momentum begins at touchend rather than ends there, so watch rather than wrap.
      watchSettle();
    }

    track.addEventListener('touchend', onTouchEnd, { passive: true });
    track.addEventListener('touchcancel', onTouchEnd, { passive: true });

    // ----- Drag-to-scroll with inertia (mirrors assets/view-more-case-studies.js) -----
    var pointerDown = false;
    var dragActive = false;
    var dragStartX = 0;
    var dragStartScroll = 0;
    var dragMoved = 0;
    var lastMoveX = 0;
    var lastMoveT = 0;
    var velocity = 0;
    var inertiaRaf = null;

    function stopInertia() {
      if (inertiaRaf !== null) {
        cancelAnimationFrame(inertiaRaf);
        inertiaRaf = null;
      }
    }

    function runInertia() {
      velocity *= 0.94;
      track.scrollLeft -= velocity * 16;
      wrap();
      if (Math.abs(velocity) > 0.05) {
        inertiaRaf = requestAnimationFrame(runInertia);
      } else {
        inertiaRaf = null;
        // Hand control back to scroll-snap so the track settles on a slide.
        track.classList.remove('is-dragging');
      }
    }

    function onDocPointerMove(e) {
      if (!pointerDown) return;
      var dx = e.clientX - dragStartX;
      if (!dragActive) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        // Threshold crossed: rebase origin so the first applied delta is 0 (no jump).
        dragActive = true;
        dragStartX = e.clientX;
        dragStartScroll = track.scrollLeft;
        lastMoveX = e.clientX;
        lastMoveT = performance.now();
        velocity = 0;
        dragMoved = 0;
        track.classList.add('is-dragging');
        return;
      }
      var now = performance.now();
      var dt = now - lastMoveT;
      if (dt > 0) {
        var instV = (e.clientX - lastMoveX) / dt; // px/ms
        velocity = velocity * 0.7 + instV * 0.3;
      }
      lastMoveX = e.clientX;
      lastMoveT = now;
      dragMoved = Math.abs(dx);
      track.scrollLeft = dragStartScroll - dx;
      // Keep the origin in step with the shift so the next move does not jump back.
      dragStartScroll += wrap();
      if (e.cancelable) e.preventDefault();
    }

    function onDocPointerUp() {
      document.removeEventListener('pointermove', onDocPointerMove);
      document.removeEventListener('pointerup', onDocPointerUp);
      document.removeEventListener('pointercancel', onDocPointerUp);
      if (!pointerDown) return;
      pointerDown = false;
      if (!dragActive) return;
      dragActive = false;
      if (Math.abs(velocity) > 0.15) {
        inertiaRaf = requestAnimationFrame(runInertia);
      } else {
        track.classList.remove('is-dragging');
      }
    }

    if (slides.length > 1) {
      track.addEventListener('pointerdown', function (e) {
        // Touch gets the browser's own momentum scrolling, which is better than ours.
        if (e.pointerType === 'touch') return;
        if (e.button !== undefined && e.button !== 0) return;
        stopInertia();
        pointerDown = true;
        dragActive = false;
        dragMoved = 0;
        velocity = 0;
        dragStartX = e.clientX;
        lastMoveX = e.clientX;
        lastMoveT = performance.now();
        dragStartScroll = track.scrollLeft;
        document.addEventListener('pointermove', onDocPointerMove);
        document.addEventListener('pointerup', onDocPointerUp);
        document.addEventListener('pointercancel', onDocPointerUp);
      });

      // Swallow the click that ends a drag so it does not activate anything underneath.
      track.addEventListener('click', function (e) {
        if (dragMoved > DRAG_THRESHOLD) {
          e.preventDefault();
          e.stopPropagation();
          dragMoved = 0;
        }
      }, true);

      // Stop the native image-ghost drag on mousedown over a slide.
      track.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
    }

    // ----- Rest with the middle slide centred, per Figma -----
    // `slides` holds only the real set, so its midpoint is the middle real slide and lands
    // inside the wrap window. The track's symmetric padding-inline is no longer what lets an
    // end slide come to rest centred, since the clones now sit either side, but it is left in
    // place so this geometry stays measured rather than assumed.
    function centreMiddleSlide() {
      var middle = slides.length < 3 ? null : slides[Math.floor(slides.length / 2)];
      if (!middle) return;
      // Set scrollLeft directly rather than scrollIntoView: this must not scroll the page.
      track.scrollLeft = Math.max(0, Math.min(centreOffsetOf(middle), maxScroll()));
    }

    requestAnimationFrame(centreMiddleSlide);
  }

  function initAll(container) {
    var scope = container && container.querySelectorAll ? container : document;
    var sliders = scope.querySelectorAll(SLIDER_SEL);
    Array.prototype.forEach.call(sliders, initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var sliders = e.target.querySelectorAll(SLIDER_SEL);
    Array.prototype.forEach.call(sliders, function (s) {
      delete s.dataset[INIT_FLAG];
    });
  });
})();
